import { Button, Input, Label, Modal } from "@/components/basic";
import FormErrorMessage from "@/components/basic/FormMessages/FormErrorMessage";
import { SelectMenuSingle } from "@/components/basic/SelectMenu/SelectMenuSingle";
import LoadingSpinner from "@/components/util/LoadingSpinner";
import { api } from "@/lib/api/api";
import { External } from "@gettactic/api";
import { IResource } from "@gettactic/api";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-hot-toast";
import { useAvigilonExternalIds } from "./useAvigilonExternalIds";

export enum AvailableExternalIdTypes {
  resources = "resources",
  office = "office",
}

export const optionsByType = {
  [AvailableExternalIdTypes.resources]: [
    { value: "ioffice", label: "iOffice" },
  ],
  [AvailableExternalIdTypes.office]: [
    { value: "avigilon_entry", label: "Avigilon Entry" },
  ],
};

type Props = {
  resources?: IResource[];
  onClose: () => void;
  invalidateQuery: () => Promise<void>;
  object_type?: AvailableExternalIdTypes;
  addedIds?: External[] | null;
};
type FormDataShape = {
  object_type: string;
  external_id: string;
};
export default function CreateOrEditExternalIDs({
  resources,
  onClose,
  invalidateQuery,
  object_type = AvailableExternalIdTypes.resources,
  addedIds,
}: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const selectIdFromList = object_type === AvailableExternalIdTypes.office;
  const options = optionsByType[object_type];

  const { refetch, data: avigilonExternalIds, isLoading } = useAvigilonExternalIds({
    id: resources?.[0]?.id || "",
    object_type,
    enabled: selectIdFromList,
    addedIds: addedIds || null,
  });

  // to refetch on open
  useEffect(() => {
    if (selectIdFromList) {
      refetch();
    }
  }, [selectIdFromList, refetch]);

  const {
    register,
    setValue,
    watch,
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<Partial<External>>({
    // TODO: Replace with Zod
    defaultValues: {
      name: options?.[0]?.value,
    },
  });

  const onSubmit = handleSubmit(async (data) => {
    setLoading(true);
    if(selectIdFromList && !data.external_id) {
      toast.error("Please select an external id");
      setLoading(false);
      return;
    }
    try {
      await api.client.externals.createExternalId({
        object_type,
        name: data.name,
        object_id: resources?.[0]?.id,
        external_id: data.external_id,
      });
      toast.success("External ID created successfully");
      onClose();
    } catch (e) {
      toast.error("Sorry we couldn't save this id");
    }
    await invalidateQuery();
    setLoading(false);
  });

  return (
    <form
      id="createOrEditHourRule"
      className="flex flex-auto flex-col overflow-hidden"
      onSubmit={(ev) => {
        ev.stopPropagation();
        onSubmit(ev);
      }}
    >
      <div>
        <div className="flex w-full flex-auto flex-col overflow-y-auto">
          <div className="mt-4 grid grid-cols-12 gap-6">
            <div className="col-span-12">
              <label htmlFor={"name"}>Name</label>
              <Controller
                name={"name"}
                control={control}
                render={({ field: { onChange, value } }) => (
                  <SelectMenuSingle
                    className="w-full lg:w-1/2"
                    dropdownClassName="max-h-[300px] overflow-y-auto"
                    setValue={onChange}
                    options={options}
                    value={value || ""}
                  />
                )}
              />
            </div>
            <div className="col-span-12">
              <Label htmlFor="external_id" className="px-1">
                <div className="text-base text-gray-800">External ID</div>
                {selectIdFromList ? (
                  isLoading ? (
                    <LoadingSpinner />
                  ) : (
                    <Controller
                      name={"external_id"}
                      control={control}
                      render={({ field: { onChange, value } }) => (
                        <SelectMenuSingle
                          className="w-full lg:w-1/2"
                          dropdownClassName="max-h-[300px] overflow-y-auto"
                          setValue={onChange}
                          options={avigilonExternalIds || [{
                            value: "",
                            label: "No external id found",
                          }]}
                          value={value || ""}
                        />
                      )}
                    />
                  )
                ) : (
                  <Input type="text" {...register("external_id")} />
                )}
              </Label>
              <FormErrorMessage errors={errors} fieldName={"name"} />
            </div>
          </div>
        </div>
      </div>
      <Modal.Footer>
        <Button
          className="w-full sm:w-auto"
          layout="squared"
          variant="white"
          font="boldGray"
          onClick={() => onClose()}
        >
          Cancel
        </Button>
        <Button
          className="mt-4 w-full sm:mt-0 sm:ml-3 sm:w-auto"
          layout="squared"
          variant="primary"
          type="submit"
          form="createOrEditHourRule"
          formTarget="createOrEditHourRule"
          disabled={loading}
        >
          {loading ? <LoadingSpinner color="text-white" /> : <span>Save</span>}
        </Button>
      </Modal.Footer>
    </form>
  );
}
